import { z } from 'zod';
import type { operations } from '@molchanovs/api-contract';
import { api } from './client';
import { ApiError } from './errors';

export const bubbleMutationOperationStatusSchema = z.enum([
  'accepted',
  'delivery_pending',
  'processing',
  'retry_scheduled',
  'applied',
  'conflict',
  'manual_reconciliation',
  'terminal_failure',
]);

export const bubbleMutationOperationReceiptSchema = z.object({
  operationId: z.uuid(),
  operationStatus: bubbleMutationOperationStatusSchema,
  replayed: z.boolean(),
  statusUrl: z.string().startsWith('/api/v1/operations/'),
  resultUrl: z.string().startsWith('/api/v1/operations/'),
});

export type BubbleMutationOperation =
  operations['getBubbleMutationOperationStatus']['responses'][200]['content']['application/json'];
const idSchema = z.uuid();

const operationStatusSchema = bubbleMutationOperationStatusSchema.or(z.literal('superseded'));
const operationSchema: z.ZodType<BubbleMutationOperation> = z.object({
  operationId: idSchema,
  operationStatus: operationStatusSchema,
  deliveryState: z.enum([
    'pending',
    'processing',
    'retrying',
    'applied',
    'superseded',
    'manual_reconciliation',
    'failed',
  ]),
  businessState: z.enum(['accepted', 'succeeded', 'failed']),
  steps: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
    superseded: z.number().int().nonnegative(),
    manualReconciliation: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  eligibility: z.object({
    nextAttemptAt: z.string().nullable(),
    blockedByEarlierOperation: z.boolean(),
    earlierOwnedOperation: z
      .object({
        operationId: idSchema,
        operationStatus: operationStatusSchema,
        familyKey: z.string(),
        operationKey: z.string(),
      })
      .nullable(),
    blockedByDependency: z.boolean(),
    runtimePaused: z.boolean(),
  }),
  resultState: z.enum(['pending', 'available', 'expired', 'unavailable']),
  resultExpiresAt: z.string().nullable(),
  compensatesOperationId: z.string().nullable(),
  deliveryAppliedAt: z.string().nullable(),
  conflictAt: z.string().nullable(),
  acceptedAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  completedAt: z.string().nullable(),
  failureCode: z.string().nullable(),
});

export async function getBubbleMutationOperation(
  operationId: string,
  signal?: AbortSignal
): Promise<BubbleMutationOperation> {
  const parsed = idSchema.safeParse(operationId);
  if (!parsed.success)
    throw new ApiError({
      status: 0,
      code: 'VALIDATION_ERROR',
      message: 'Invalid operation identifier.',
    });
  return api.get(`/api/v1/operations/${encodeURIComponent(parsed.data)}`, {
    signal,
    schema: operationSchema,
  });
}
